import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { mergeClasses } from '@fluentui/react-components';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { Collapse, useCollapseState, collapseExpander, getPanelErrorProps as getExtraProps } from '@bingads-webui-campaign/wizard-jumbo-card';
import { useFormService, FORM_STATES } from '@bingads-webui-fluent-component/form';
import { ValidationGroup } from '@bingads-webui-react/validation';
import {
  CampaignPanel,
  CompanyPageCard,
} from '@bingads-webui-campaign-react/linkedin-ad';
import {
  BidStrategyCard,
  BudgetCard,
} from '@bingads-webui-campaign-react/linkedin-budget';
import {
  CollapsibleLocations,
  CollapsibleLanguage,
  CollapsibleAudiences,
  CollapsibleAge,
  CollapsibleGender,
  CollapsibleConversionGoals,
} from '@bingads-webui-campaign-react/linkedin-targeting';
import { MemberTokenWarning } from '@bingads-webui-campaign-react/linkedin-shared-components';

import { useLinkedInCampaignSettingsStyles } from './styles/linkedin-campaign-settings-styles';

const UnmappedFormServerErrors = withControlErrorHandling(UnmappedFormErrors);

const BudgetCards = ['budget', 'bid-strategy'];
const TargetingCards = ['locations', 'language', 'audiences', 'gender', 'age', 'conversion-goals'];

export const LinkedInCampaingSettings = ({ data, error, onDataChange }) => {
  const classes = useLinkedInCampaignSettingsStyles();

  const { scope } = useContext(ScopeContext);

  const {
    campaign = {},
    companyInfo = {},
    budget = {},
    ageRanges = [],
    genders = [],
    defaultLocation,
    hasLinkedInCompatibleGoals,
    isMemberTokenRevoked,
  } = data || {};

  const { serverErrors = [] } = error || {};

  const { state } = useFormService();
  const { context: { saveFailed } } = state;
  const isSaving = state.matches(FORM_STATES.SAVING);
  const isLoading = state.matches(FORM_STATES.LOADING);

  const showAllValidationErrors = !_.isEmpty(serverErrors) || saveFailed;
  const areBeaconsActive = showAllValidationErrors && !isSaving;

  const [
    targetingCards,
    expandedTargetingCards,
    setTargetingExpandedCards,
  ] = useCollapseState(TargetingCards, TargetingCards);

  const expandTargetingCard = useMemo(() =>
    collapseExpander({ setActiveKeys: setTargetingExpandedCards }), [setTargetingExpandedCards]);

  const [
    budgetCards,
    expandedBudgetCards,
    setBudgetExpandedCards,
  ] = useCollapseState(BudgetCards, BudgetCards);

  const expandBudgetCard = useMemo(() =>
    collapseExpander({ setActiveKeys: setBudgetExpandedCards }), [setBudgetExpandedCards]);

  const onValidateBudget = useCallback((result) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        budget: {
          ...prevData.budget,
          ...result.data,
        },
      }),
      isValid: result.isValid,
    });
  }, [onDataChange]);

  const onValidate = useCallback((result) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        ...result.data,
      }),
      isValid: result.isValid,
    });
  }, [onDataChange]);

  const navigateToConversionGoals = useCallback(() => {
    scope.save('/campaign/vnext/conversiongoals');
  }, [scope]);

  const campaignSection = useMemo(() => (
    <div className={classes.cardContainer}>
      <CampaignPanel
        campaign={campaign}
        onDataChange={onDataChange}
      />
      <CompanyPageCard
        companyInfo={companyInfo}
      />
    </div>
  ), [campaign, classes.cardContainer, companyInfo, onDataChange]);

  const budgetSection = useMemo(() => (
    <ValidationGroup
      validationProps={{
        stopPropagation: true,
        onValidate: onValidateBudget,
      }}
    >
      <Collapse
        className={classes.cardContainer}
        allPanelItems={budgetCards}
        activeKeys={expandedBudgetCards}
        setActiveKeys={setBudgetExpandedCards}
        skipItemsTypeCheck
      >
        <BudgetCard
          key="budget"
          className="budget-card"
          data={budget}
          attentionBeaconProps={{ getExtraProps }}
          actuationProps={{
            expandKey: 'budget',
            expand: expandBudgetCard,
          }}
        />
        <BidStrategyCard
          key="bid-strategy"
          className="bid-strategy-card"
          data={budget}
          attentionBeaconProps={{ getExtraProps }}
          actuationProps={{
            expandKey: 'bid-strategy',
            expand: expandBudgetCard,
          }}
        />
      </Collapse>
    </ValidationGroup>
  ), [onValidateBudget, classes.cardContainer, budgetCards, expandedBudgetCards, setBudgetExpandedCards, budget, expandBudgetCard]);

  const targetingSection = useMemo(() => (
    <Collapse
      className={classes.cardContainer}
      allPanelItems={targetingCards}
      activeKeys={expandedTargetingCards}
      setActiveKeys={setTargetingExpandedCards}
      skipItemsTypeCheck
    >
      <CollapsibleLocations
        key="locations"
        className="locations-card"
        defaultLocation={defaultLocation}
        isLoading={isLoading}
        attentionBeaconProps={{ getExtraProps }}
        actuationProps={{
          expandKey: 'locations',
          expand: expandTargetingCard,
        }}
      />
      <CollapsibleLanguage
        key="language"
        className="language-card"
        isLoading={isLoading}
        isDisabled // Currently, language isn't editable
        attentionBeaconProps={{ getExtraProps }}
        actuationProps={{
          expandKey: 'language',
          expand: expandTargetingCard,
        }}
      />
      <CollapsibleAudiences
        key="audiences"
        className="audiences-card"
        isLoading={isLoading}
        attentionBeaconProps={{ getExtraProps }}
        actuationProps={{
          expandKey: 'audiences',
          expand: expandTargetingCard,
        }}
      />
      <CollapsibleGender
        key="gender"
        className="gender-card"
        genders={genders}
        isLoading={isLoading}
        attentionBeaconProps={{ getExtraProps }}
        actuationProps={{
          expandKey: 'gender',
          expand: expandTargetingCard,
        }}
      />
      <CollapsibleAge
        key="age"
        className="age-card"
        ageRanges={ageRanges}
        isLoading={isLoading}
        attentionBeaconProps={{ getExtraProps }}
        actuationProps={{
          expandKey: 'age',
          expand: expandTargetingCard,
        }}
      />
      <CollapsibleConversionGoals
        key="conversion-goals"
        className="conversion-goals-card"
        isEditMode
        hasLinkedInCompatibleGoals={hasLinkedInCompatibleGoals}
        isLoading={isLoading}
        onNavigateToConversionGoalsClicked={navigateToConversionGoals}
        attentionBeaconProps={{ getExtraProps }}
        actuationProps={{
          expandKey: 'conversion-goals',
          expand: expandTargetingCard,
        }}
      />
    </Collapse>
  ), [classes.cardContainer, targetingCards, expandedTargetingCards, setTargetingExpandedCards, defaultLocation, isLoading, expandTargetingCard, genders, ageRanges, hasLinkedInCompatibleGoals, navigateToConversionGoals]);

  if (!data) { return null; }

  return (
    <div className={mergeClasses('li-campaign-settings-step', classes.cardContainer)}>
      <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <ValidationGroup validationProps={{ data, onValidate }}>
              <UnmappedFormServerErrors className="unmapped-server-errors" />
              {isMemberTokenRevoked && <MemberTokenWarning /> }
              {campaignSection}
              {budgetSection}
              {targetingSection}
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

LinkedInCampaingSettings.propTypes = {
  data: PropTypes.shape({}).isRequired,
  error: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
};
